import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "outer"
}
const _hoisted_2 = {
  key: 0,
  class: "title"
}
const _hoisted_3 = {
  key: 1,
  class: "description"
}
const _hoisted_4 = {
  key: 1,
  class: "shared-by"
}
const _hoisted_5 = { class: "section-title" }
const _hoisted_6 = { class: "top-field" }
const _hoisted_7 = { class: "name" }
const _hoisted_8 = {
  key: 2,
  class: "people"
}
const _hoisted_9 = { class: "section-title" }
const _hoisted_10 = { key: 3 }
const _hoisted_11 = { class: "section-title" }
const _hoisted_12 = { class: "section-title" }
const _hoisted_13 = { class: "icon" }
const _hoisted_14 = { class: "text" }
const _hoisted_15 = ["href"]
const _hoisted_16 = { class: "title" }
const _hoisted_17 = {
  key: 1,
  class: "title"
}
const _hoisted_18 = { class: "subtitle" }
const _hoisted_19 = {
  key: 0,
  class: "edit"
}
const _hoisted_20 = {
  key: 4,
  class: "map"
}
const _hoisted_21 = {
  key: 1,
  class: "loading-icon fill-block"
}
const _hoisted_22 = { key: 2 }

import { ref, computed, watch, defineAsyncComponent, markRaw } from 'vue';
import type { Component } from 'vue';
import { useRoute } from 'vue-router';

import NcActions from '@nextcloud/vue/components/NcActions';
import NcActionButton from '@nextcloud/vue/components/NcActionButton';
import axios from '@nextcloud/axios';
import { getCanonicalLocale } from '@nextcloud/l10n';
import { DateTime } from 'luxon';

import { config } from '@services/user-config';
import { useRouteIs } from '@services/route-is';

import Cluster from '@components/frame/Cluster.vue';
import AlbumsList from '@components/modal/AlbumsList.vue';
import XLoadingIcon from '@components/XLoadingIcon.vue';
import EditIcon from 'vue-material-design-icons/Pencil.vue';
import CalendarIcon from 'vue-material-design-icons/Calendar.vue';
import CameraIrisIcon from 'vue-material-design-icons/CameraIris.vue';
import ImageIcon from 'vue-material-design-icons/Image.vue';
import LocationIcon from 'vue-material-design-icons/MapMarker.vue';
import TagIcon from 'vue-material-design-icons/Tag.vue';

import { t } from '@services/l10n';
import { cacheData, getCachedData } from '@services/cache';
import { isAbortError, useAbort } from '@services/utils/abort-vue';
import * as utils from '@services/utils/common';
import * as dav from '@services/dav';

import type { IImageInfo, IPhoto, IExif } from '@typings';
import type { IFolder, INode, IView } from '@nextcloud/files';

interface TopField {
  id: string;
  title: string;
  subtitle: string[];
  icon: Component;
  href?: string;
  edit?: () => void;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'Metadata',
  props: {
    node: {},
    active: { type: Boolean },
    folder: {},
    view: {}
  },
  setup(__props: any, { expose: __expose }) {

const NcAvatar = defineAsyncComponent(() => import('@nextcloud/vue/components/NcAvatar'));

const MapStandalone = defineAsyncComponent(() => import('@components/MapStandalone.vue'));

const props = __props;
const fileid = ref<number | null>(null);
const filename = ref('');
const route = useRoute();
const routeIs = useRouteIs();
const exif = ref({} as IExif);
const baseInfo = ref({} as IImageInfo);
const error = ref(false);

const loading = ref(0);
const abort = useAbort();

/** Whether the current user may edit this file */
const canEdit = computed(() => baseInfo.value?.permissions?.includes('U'));

/** Title EXIF value */
const title = computed(() => exif.value.Title || null);

/** Description EXIF value */
const description = computed(() => exif.value.Description || null);

/** Date taken info */
const dateOriginal = computed(() => {
  // Try to get timezone info
  let dateWithTz: DateTime | null = null;
  const valid = () => dateWithTz?.isValid;

  // If we have an actual epoch, we can shift the date to the correct timezone
  if (!valid() && exif.value.DateTimeEpoch) {
    const date = DateTime.fromSeconds(exif.value.DateTimeEpoch);
    if (date.isValid) {
      const tzOffset = exif.value.OffsetTimeOriginal || exif.value.OffsetTime; // e.g. -05:00
      const tzId = exif.value.LocationTZID; // e.g. America/New_York

      // Use timezone offset if available
      if (!valid() && tzOffset) {
        dateWithTz = date.setZone(`UTC${tzOffset}`);
      }

      // Fall back to tzId
      if (!valid() && tzId) {
        dateWithTz = date.setZone(tzId);
      }
    }
  }

  // If tz info is unavailable / wrong, we will show the local time only
  // In this case, use the datetaken instead, which is guaranteed to be local, shifted to UTC
  if (!valid() && baseInfo.value.datetaken) {
    const date = DateTime.fromSeconds(baseInfo.value.datetaken);
    if (date.isValid) {
      dateWithTz = date.setZone('UTC');
    }
  }

  // Return only if we found a valid date
  return valid() ? dateWithTz : null;
});

/** Localized long date string */
const dateOriginalStr = computed(() => utils.getLongDateStr(new Date(baseInfo.value.datetaken * 1000), true));

/** Localized time string, with zone when known */
const dateOriginalTime = computed(() => {
  if (!dateOriginal.value) return null;

  const fields: (keyof IExif)[] = ['OffsetTimeOriginal', 'OffsetTime', 'LocationTZID'];
  const hasTz = fields.some((key) => exif.value[key]);

  const format = 't' + (hasTz ? ' ZZ' : '');

  return [dateOriginal.value.toFormat(format, { locale: getCanonicalLocale() })];
});

/** Camera make and model info */
const camera = computed(() => {
  const make = exif.value.Make;
  const model = exif.value.Model;
  if (!make || !model) return null;
  if (model.startsWith(make)) return model;
  return `${make} ${model}`;
});

/** Aperture, shutter, focal length and ISO parts */
const cameraSub = computed(() => {
  const f = exif.value.FNumber || exif.value.Aperture;
  const s = shutterSpeed.value;
  const len = exif.value.FocalLength;
  const iso = exif.value.ISO;

  const parts: string[] = [];
  if (f) parts.push(`f/${f}`);
  if (s) parts.push(`${s}`);
  if (len) parts.push(`${len}mm`);
  if (iso) parts.push(`ISO${iso}`);
  return parts;
});

/** Convert shutter speed decimal to 1/x format */
const shutterSpeed = computed(() => {
  const speed = Number(exif.value.ShutterSpeedValue || exif.value.ShutterSpeed || exif.value.ExposureTime);
  if (!speed) return null;

  if (speed < 1) {
    return `1/${Math.round(1 / speed)}`;
  } else {
    return `${Math.round(speed * 10) / 10}s`;
  }
});

/** Image info */
const imageInfoTitle = computed(() => {
  if (config.sidebar_filepath && filepath.value) {
    return filepath.value.replace(/^\//, ''); // remove leading slash
  }

  return baseInfo.value.basename;
});

/** Path to file excluding user directory */
const filepath = computed(() => baseInfo.value?.filename ?? null);

/** Dimensions and megapixel parts */
const imageInfoSub = computed(() => {
  const parts: string[] = [];
  let mp = Number(exif.value.Megapixels);

  if (baseInfo.value.w && baseInfo.value.h) {
    parts.push(`${baseInfo.value.w}x${baseInfo.value.h}`);

    if (!mp) {
      mp = (baseInfo.value.w * baseInfo.value.h) / 1000000;
    }
  }

  if (mp) {
    parts.unshift(`${mp.toFixed(1)}MP`);
  }

  return parts;
});

/** GPS latitude as number */
const lat = computed(() => Number(exif.value.GPSLatitude));

/** GPS longitude as number */
const lon = computed(() => Number(exif.value.GPSLongitude));

/** Human address, falling back to coordinates */
const address = computed(() => {
  if (baseInfo.value.address) {
    return baseInfo.value.address;
  }

  if (lat.value && lon.value) {
    return `${lat.value.toFixed(6)}, ${lon.value.toFixed(6)}`;
  }

  return undefined;
});

/** Localized tag names */
const tagNames = computed(() => Object.values(baseInfo.value?.tags || {}).map((tag: string) => t('recognize', tag)));

/** Comma-joined tag names */
const tagNamesStr = computed(() => (tagNames.value.length > 0 ? tagNames.value.join(', ') : null));

/** OpenStreetMap link for the coordinates */
const mapFullUrl = computed(
  () => `https://www.openstreetmap.org/?mlat=${lat.value}&mlon=${lon.value}#map=18/${lat.value}/${lon.value}`,
);

/** Albums containing this file, minus hidden ones unless configured */
const albums = computed(() => {
  let list = baseInfo.value?.clusters?.albums ?? [];

  // Filter out hidden albums
  if (!config.show_hidden_albums) {
    list = list.filter((a) => !a.name.startsWith('.'));
  }

  return list;
});

/** Faces for this file, backend depends on route and config */
const people = computed(() => {
  const clusters = baseInfo.value?.clusters;

  // force face-recognition on its own route, or if recognize is disabled
  if (routeIs.FaceRecognition || !config.recognize_enabled) {
    return clusters?.facerecognition ?? [];
  }

  return clusters?.recognize ?? [];
});

/** Whether this file is shared by someone else */
const isShared = computed(() => !!baseInfo.value.owneruid && baseInfo.value.owneruid !== utils.uid);

/** Rows shown in the metadata section */
const topFields = computed(() => {
  const list: TopField[] = [];

  if (dateOriginal.value) {
    list.push({
      id: 'date',
      title: dateOriginalStr.value!,
      subtitle: dateOriginalTime.value!,
      icon: markRaw(CalendarIcon),
      edit: editDate,
    });
  }

  if (camera.value) {
    list.push({
      id: 'camera',
      title: camera.value,
      subtitle: cameraSub.value,
      icon: markRaw(CameraIrisIcon),
    });
  }

  if (imageInfoTitle.value) {
    list.push({
      id: 'image-info', // adds class
      title: imageInfoTitle.value,
      subtitle: imageInfoSub.value,
      icon: markRaw(ImageIcon),
      href: filepath.value
        ? dav.viewInFolderUrl({
            fileid: fileid.value!,
            filename: filepath.value,
          })
        : undefined,
    });
  }

  if (tagNamesStr.value) {
    list.push({
      id: 'tags',
      title: tagNamesStr.value,
      subtitle: [],
      icon: markRaw(TagIcon),
      edit: editTags,
    });
  }

  if (address.value || canEdit.value) {
    list.push({
      id: 'location',
      title: address.value || t('memories', 'No coordinates'),
      subtitle: address.value ? [] : [t('memories', 'Click edit to set location')],
      icon: markRaw(LocationIcon),
      href: address.value ? mapFullUrl.value : undefined,
      edit: editGeo,
    });
  }

  return list;
});

/** Reset state unless the given file is already current */
function invalidateUnless(id: number): AbortSignal | null {
  if (fileid.value === id) return null;
  const signal = abort.renew();
  error.value = false;
  fileid.value = null;
  exif.value = {};
  return signal;
}

/** Load metadata for a photo, cache-first then server */
async function update(photo: number | IPhoto): Promise<IImageInfo | null> {
  const signal = invalidateUnless(0)!;

  // Use a consistent URL for metadata.
  const url = utils.getImageInfoUrl(photo, config, { route });

  // Helper to apply additional fields.
  const applyImageInfo = (data: IImageInfo) => {
    baseInfo.value = data;
    fileid.value = data.fileid;
    filename.value = data.basename;
    exif.value = data.exif ?? {};
  };

  // Attempt to get it from the cache first.
  let wasCached = false;
  try {
    const cached = await getCachedData<IImageInfo>(url, { signal });
    signal.throwIfAborted();
    if (cached) {
      applyImageInfo(cached);
      wasCached = true;
    }
  } catch (e) {
    if (isAbortError(e)) return null;
  }

  // Always refresh the metadata from server.
  try {
    loading.value++;
    const res = await axios.get<IImageInfo>(url, { signal });
    signal.throwIfAborted();
    applyImageInfo(res.data);
    cacheData(url, res.data);
  } catch (err) {
    if (isAbortError(err)) return null;
    if (wasCached) {
      error.value = false;
    } else {
      error.value = true;
      throw err;
    }
  } finally {
    loading.value--;
  }

  return baseInfo.value;
}

/** Reload metadata for the current file */
async function refresh() {
  if (fileid.value) await update(fileid.value);
}

/** Open the edit dialog on the date section */
function editDate() {
  _m.modals.editMetadata([_m.viewer.currentPhoto!], [1]);
}

/** Open the edit dialog on the tags section */
function editTags() {
  _m.modals.editMetadata([_m.viewer.currentPhoto!], [2]);
}

/** Open the edit dialog on the EXIF section */
function editEXIF() {
  _m.modals.editMetadata([_m.viewer.currentPhoto!], [3]);
}

/** Open the edit dialog on the location section */
function editGeo() {
  _m.modals.editMetadata([_m.viewer.currentPhoto!], [4]);
}

/** Refresh when the current file changes on disk */
function handleFileUpdated({ fileid: updated }: utils.BusEvent['files:file:updated']) {
  if (updated && fileid.value === updated) {
    refresh();
  }
}

utils.useBus('files:file:updated', handleFileUpdated);
utils.useBus('memories:albums:update', refresh);

watch(
  () => props.node,
  () => {
    const id = Number(props.node?.fileid ?? props.node?.id ?? 0);
    if (id) {
      update(id);
    }
  },
  { immediate: true },
);

__expose({ fileid, update, invalidateUnless });

return (_ctx: any,_cache: any) => {
  return (fileid.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        (title.value || description.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "exif-head",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (editEXIF()))
            }, [
              (title.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(title.value), 1))
                : _createCommentVNode("", true),
              (description.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(description.value), 1))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true),
        (isShared.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, _toDisplayString(_unref(t)('memories', 'Shared By')), 1),
              _createElementVNode("div", _hoisted_6, [
                _createVNode(_unref(NcAvatar), {
                  key: "uid",
                  user: baseInfo.value.owneruid,
                  showUserStatus: false,
                  size: 24
                }, null, 8, ["user"]),
                _createElementVNode("span", _hoisted_7, _toDisplayString(baseInfo.value.ownername), 1)
              ])
            ]))
          : _createCommentVNode("", true),
        (people.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _createElementVNode("div", _hoisted_9, _toDisplayString(_unref(t)('memories', 'People')), 1),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(people.value, (face) => {
                return (_openBlock(), _createElementBlock("div", {
                  class: "container",
                  key: face.cluster_id
                }, [
                  _createVNode(Cluster, {
                    class: "cluster--rounded",
                    data: face,
                    counters: false
                  }, null, 8, ["data"])
                ]))
              }), 128))
            ]))
          : _createCommentVNode("", true),
        (albums.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              _createElementVNode("div", _hoisted_11, _toDisplayString(_unref(t)('memories', 'Albums')), 1),
              _createVNode(AlbumsList, {
                class: "albums",
                albums: albums.value
              }, null, 8, ["albums"])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_12, _toDisplayString(_unref(t)('memories', 'Metadata')), 1),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(topFields.value, (field) => {
          return (_openBlock(), _createElementBlock("div", {
            key: field.title,
            class: _normalizeClass(`top-field top-field--${field.id}`)
          }, [
            _createElementVNode("div", _hoisted_13, [
              (_openBlock(), _createBlock(_resolveDynamicComponent(field.icon), { size: 24 }))
            ]),
            _createElementVNode("div", _hoisted_14, [
              (field.href)
                ? (_openBlock(), _createElementBlock("a", {
                    key: 0,
                    href: field.href,
                    target: "_blank",
                    rel: "noopener noreferrer"
                  }, [
                    _createElementVNode("span", _hoisted_16, _toDisplayString(field.title), 1)
                  ], 8, _hoisted_15))
                : (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(field.title), 1)),
              (field.subtitle.length)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                    _cache[1] || (_cache[1] = _createElementVNode("br", null, null, -1)),
                    _createElementVNode("span", _hoisted_18, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(field.subtitle, (part) => {
                        return (_openBlock(), _createElementBlock("span", {
                          class: "part",
                          key: part
                        }, _toDisplayString(part), 1))
                      }), 128))
                    ])
                  ], 64))
                : _createCommentVNode("", true)
            ]),
            (canEdit.value && field.edit)
              ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                  _createVNode(_unref(NcActions), { inline: 1 }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(NcActionButton), {
                        "aria-label": _unref(t)('memories', 'Edit'),
                        onClick: ($event: any) => (field.edit?.())
                      }, {
                        icon: _withCtx(() => [
                          _createVNode(EditIcon, { size: 20 })
                        ]),
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref(t)('memories', 'Edit')) + " ", 1)
                        ]),
                        _: 1
                      }, 8, ["aria-label", "onClick"])
                    ]),
                    _: 2
                  }, 1024)
                ]))
              : _createCommentVNode("", true)
          ], 2))
        }), 128)),
        (lat.value && lon.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
              _createVNode(_unref(MapStandalone), {
                center: [lat.value, lon.value],
                pins: [[lat.value, lon.value]]
              }, null, 8, ["center", "pins"])
            ]))
          : _createCommentVNode("", true)
      ]))
    : (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
          _createVNode(XLoadingIcon)
        ]))
      : (error.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_22, _toDisplayString(_unref(t)('memories', 'Failed to load metadata')), 1))
        : _createCommentVNode("", true)
}
}

})